import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Lineage-bound workflow API token used by external systems to start
 * workflow runs via `/api/v1/workflow-triggers/:workflowId/start`.
 *
 * Tokens are 1:1 with a workflow lineage at create time and stay bound for
 * life. Multiple named tokens per workflow are allowed (e.g. one per
 * external integration partner) so credentials can be revoked independently.
 */
const WorkflowApiTokenSchema = z.object({
  id: z.string().uuid(),
  workflowId: z.string().uuid(),
  name: z.string(),
  keyPrefix: z.string(),
  lastFourChars: z.string(),
  maskedKey: z.string(),
  createdBy: z.object({ id: z.string(), name: z.string() }).nullable(),
  lastUsedAt: z.string().nullable(),
  usageCount: z.number(),
  revokedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const ListWorkflowApiTokensInputSchema = z.object({
  workflowId: z.string().uuid("Invalid workflow ID format"),
});

const CreateWorkflowApiTokenInputSchema = z.object({
  workflowId: z.string().uuid("Invalid workflow ID format"),
  name: z.string().min(1, "Name is required").max(255, "Name too long"),
});

/**
 * Response shape for `create`. The plaintext token is returned EXACTLY ONCE
 * — the caller must surface it immediately and never persist it server-side.
 */
const CreateWorkflowApiTokenResponseSchema = z.object({
  id: z.string().uuid(),
  workflowId: z.string().uuid(),
  name: z.string(),
  keyPrefix: z.string(),
  lastFourChars: z.string(),
  createdAt: z.string(),
  fullKey: z.string(),
});

const RevokeWorkflowApiTokenInputSchema = z.object({
  id: z.string().uuid("Invalid token ID format"),
});

export const workflowApiTokensContracts = {
  /**
   * List all (revoked + active) tokens for a workflow lineage.
   *
   * Returns metadata only — no hashes, no plaintext. Multiple named tokens
   * per workflow are supported (e.g. `"NetSuite prod"`,
   * `"Snowflake staging"`).
   */
  list: oc
    .input(ListWorkflowApiTokensInputSchema)
    .output(z.object({ tokens: z.array(WorkflowApiTokenSchema) }))
    .route({
      summary: "List API tokens for a workflow",
      description:
        "Returns active and revoked external-trigger API tokens for a workflow with name, masked key, creator, last use and usage count; never the secret.",
      method: "GET",
    }),

  /**
   * Create a new lineage-bound token. Returns plaintext **once**.
   *
   * Rejects (advisory gate) if the lineage has no pinned version, or the
   * pinned version does not contain a single `http_trigger` step. The
   * external start handler re-checks all gates on every request, so this is
   * a UX guard rather than a security boundary.
   */
  create: oc
    .input(CreateWorkflowApiTokenInputSchema)
    .output(CreateWorkflowApiTokenResponseSchema)
    .route({
      summary: "Create an API token for a workflow",
      description:
        "Creates a named token that lets an external system start this workflow; returns the full key once. Requires a pinned version with one HTTP trigger step.",
      method: "POST",
    }),

  /**
   * Revoke a token. Idempotent: a second call against an already-revoked
   * token returns 404.
   */
  revoke: oc
    .input(RevokeWorkflowApiTokenInputSchema)
    .output(z.object({ success: z.literal(true) }))
    .route({
      summary: "Revoke a workflow API token",
      description:
        "Revokes one workflow API token so external systems can no longer start runs with it.",
      method: "POST",
    }),
} as const;

export type WorkflowApiTokenDTO = z.infer<typeof WorkflowApiTokenSchema>;
export type ListWorkflowApiTokensInput = z.infer<
  typeof ListWorkflowApiTokensInputSchema
>;
export type CreateWorkflowApiTokenInput = z.infer<
  typeof CreateWorkflowApiTokenInputSchema
>;
export type CreateWorkflowApiTokenResponse = z.infer<
  typeof CreateWorkflowApiTokenResponseSchema
>;
